import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import useSeo from '../hooks/useSeo';
import Footer from '../components/Footer';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { 
  Shield, Heart, Eye, Lightbulb, Users,
  Search, CheckCircle, FileText, ClipboardList, Newspaper,
  ArrowRight
} from 'lucide-react';

const ABOUT_STRINGS = {
  aboutKMS: "About KMS",
  heroTitle: "Simplifying access to government schemes for every citizen",
  heroDesc: "Know My Schemes (KMS) is India's trusted welfare assistance platform. We believe every citizen deserves easy access to the government benefits they're entitled to.",
  ourMission: "Our Mission",
  missionDesc: "Our platform bridges the gap between citizens and government welfare programs, making scheme discovery, eligibility verification, and application support seamless and accessible to all.",
  feat1: "Comprehensive scheme database",
  feat2: "Real-time eligibility check",
  feat3: "Document checklist generator",
  feat4: "Application tracking",
  ourValues: "Our Values",
  valuesDriveUs: "What drives us every day",
  integrity: "Integrity",
  integrityDesc: "Honesty and transparency in all dealings.",
  service: "Service",
  serviceDesc: "Dedicated to serving citizens with care.",
  transparency: "Transparency",
  transparencyDesc: "Clear communication at every step.",
  compassion: "Compassion",
  compassionDesc: "Empathy for every citizen's needs.",
  innovation: "Innovation",
  innovationDesc: "Continuously improving to serve better.",
  ourServices: "Our Services",
  howKMSHelps: "How KMS helps citizens",
  schemeDiscovery: "Scheme Discovery",
  schemeDiscoveryDesc: "Find government schemes tailored to your eligibility.",
  eligibilityInsights: "Eligibility Insights",
  eligibilityInsightsDesc: "Understand if you qualify for welfare programs.",
  docGuidance: "Document Guidance",
  docGuidanceDesc: "Know exactly what documents you need.",
  appSupport: "Application Support",
  appSupportDesc: "Get help throughout the application process.",
  latestNews: "Latest National News",
  latestNewsDesc: "Stay informed with news updates every 6 hours.",
  quote: "Our mission is to ensure that no eligible citizen misses out on government welfare benefits due to lack of information or complex application processes.",
  kmsTeam: "KMS Team",
  readyMakeDiff: "Ready to make a difference?",
  joinTeam: "Join our growing team and help us empower millions of citizens with the welfare benefits they deserve.",
  viewCareers: "View Career Opportunities",
};

const AboutPage = () => {
  useSeo({
    title: 'About KnowMySchemes — Indian Government Schemes & Jobs Portal',
    description: 'Learn about KnowMySchemes — a privately operated digital platform that helps Indian citizens discover government welfare schemes, browse government job notifications, and use AI-powered policy impact analysis.',
    canonical: 'https://knowmyschemes.com/about',
  });
  const ui = useTranslatedUI(ABOUT_STRINGS);

  const values = [
    { icon: Shield, title: ui.integrity, description: ui.integrityDesc },
    { icon: Heart, title: ui.service, description: ui.serviceDesc },
    { icon: Eye, title: ui.transparency, description: ui.transparencyDesc },
    { icon: Users, title: ui.compassion, description: ui.compassionDesc },
    { icon: Lightbulb, title: ui.innovation, description: ui.innovationDesc },
  ];

  const services = [
    { icon: Search, title: ui.schemeDiscovery, description: ui.schemeDiscoveryDesc },
    { icon: CheckCircle, title: ui.eligibilityInsights, description: ui.eligibilityInsightsDesc },
    { icon: FileText, title: ui.docGuidance, description: ui.docGuidanceDesc },
    { icon: ClipboardList, title: ui.appSupport, description: ui.appSupportDesc },
    { icon: Newspaper, title: ui.latestNews, description: ui.latestNewsDesc },
  ];

  const features = [ui.feat1, ui.feat2, ui.feat3, ui.feat4];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <p className="text-green-200 text-sm font-medium mb-2">{ui.aboutKMS}</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              {ui.heroTitle}
            </h1>
            <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto">
              {ui.heroDesc}
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              {ui.ourMission}
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              {ui.missionDesc}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1563457039413-0c39b8e8963d?auto=compress&cs=tinysrgb&w=800"
              alt="Indian team working together"
              className="rounded-2xl shadow-xl w-full"
            />
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-green-600 font-medium mb-2">{ui.ourValues}</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              {ui.valuesDriveUs}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 hover:bg-green-50 transition-colors">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-sm text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <p className="text-green-600 font-medium mb-2">{ui.ourServices}</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
            {ui.howKMSHelps}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
          {services.map((service, index) => (
            <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow border border-gray-100">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <service.icon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{service.title}</h3>
              <p className="text-sm text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quote Section */}
      <div className="bg-green-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <blockquote className="text-xl md:text-2xl italic mb-6">
            "{ui.quote}"
          </blockquote>
          <p className="text-green-200">— {ui.kmsTeam}</p>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          {ui.readyMakeDiff}
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          {ui.joinTeam}
        </p>
        <Link 
          to="/careers"
          className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors"
        >
          {ui.viewCareers}
          <ArrowRight className="w-5 h-5" />
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
